import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';

/**
 * ENG-5981: shows one assistant tool result as a card that fits what came back —
 * people, menu items, printers, sales tiles, store status, hours, delivery apps, website —
 * with a tidy fallback for anything else. Used by the console panel and the phone page.
 */
type Kind = 'people' | 'person' | 'printer' | 'perms' | 'menu' | 'printers' | 'sales' | 'status' | 'hours' | 'holidays' | 'delivery'
  | 'storefront' | 'done' | 'error' | 'list' | 'fields' | 'none';

const READ_TOOL = /^(search_|get_|list_)/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface Row { [key: string]: any; }

const PAGE = 8;

@Component({
  selector: 'app-agent-result',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './agent-result.component.html',
  styleUrls: ['./agent-result.component.scss'],
})
export class AgentResultComponent implements OnChanges {
  @Input() action: any;
  @Output() undo = new EventEmitter<any>();

  kind: Kind = 'none';
  title = '';
  icon = 'check_circle';
  rows: Row[] = [];
  columns: string[] = [];
  fields: { label: string; value: any; swatch?: boolean }[] = [];
  groups: { name: string; items: string[] }[] = [];
  nameKey = 'name';
  tiles: { label: string; value: string }[] = [];
  doneText = '';
  showAll = false;
  filter = '';

  ngOnChanges() {
    this.build();
  }

  get result(): any {
    return this.action?.result;
  }

  get visibleRows(): Row[] {
    const q = (this.filter || '').trim().toLowerCase();
    const rows = q ? this.rows.filter(r => JSON.stringify(r).toLowerCase().includes(q)) : this.rows;
    return this.showAll || q ? rows : rows.slice(0, PAGE);
  }

  get hiddenCount(): number {
    return this.filter ? 0 : Math.max(0, this.rows.length - PAGE);
  }

  get canUndo(): boolean {
    return !!this.action?.undo_id && !this.action?.undoState;
  }

  initials(name: string): string {
    const parts = String(name || '?').trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
  }

  money(v: any): string {
    const n = Number(v);
    return v === null || v === undefined || v === '' || isNaN(n) ? '—' :
      '$' + n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  }

  roleLabel(role: string): string {
    return String(role || '').replace(/_/g, ' ').trim() || 'No role';
  }

  printerOnline(p: Row): boolean | null {
    const s = p?.status;
    if (s === undefined || s === null) { return null; }
    if (typeof s === 'object') {
      if ('online' in s) { return !!s.online; }
      if ('state' in s) { return /online|ok|ready/i.test(String(s.state)); }
    }
    return /online|ok|ready/i.test(String(s));
  }

  cell(row: Row, col: string): string {
    const v = row?.[col];
    if (v === null || v === undefined || v === '') { return '—'; }
    if (typeof v === 'boolean') { return v ? 'Yes' : 'No'; }
    if (Array.isArray(v)) { return String(v.length); }
    if (typeof v === 'object') { return v.name || v.title || '—'; }
    return this.when(String(v)).toString().replace(/_/g, ' ');
  }

  label(key: string): string {
    return String(key || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  private build() {
    const tool = this.action?.tool || '';
    const r = this.result;
    this.rows = []; this.columns = []; this.fields = []; this.tiles = []; this.doneText = ''; this.groups = [];
    this.showAll = false; this.filter = '';
    this.title = this.label(tool);

    if (r && typeof r === 'object' && r.error) {
      this.kind = 'error'; this.icon = 'error_outline';
      this.doneText = typeof r.error === 'string' ? r.error : JSON.stringify(r.error);
      return;
    }
    if (r?.status === 'needs_confirmation') { this.kind = 'none'; return; }

    const results: Row[] = Array.isArray(r?.results) ? r.results : null;
    if (tool === 'search_employees' && results) {
      this.kind = 'people'; this.icon = 'group'; this.rows = results;
      this.title = `${results.length} ${results.length === 1 ? 'person' : 'people'}`;
      return;
    }
    if (tool === 'get_employee' && r?.id) {
      this.kind = 'person'; this.icon = 'badge'; this.title = 'Employee';
      this.fields = this.personFields(r);
      return;
    }
    if ((tool === 'get_employee_permissions' || tool === 'list_permissions')
        && (r?.permissions || r?.groups) && typeof (r.permissions || r.groups) === 'object') {
      const g = r.permissions || r.groups;
      this.kind = 'perms'; this.icon = 'verified_user';
      this.groups = Object.keys(g).filter(k => Array.isArray(g[k]) && g[k].length)
        .map(k => ({name: this.pretty(k), items: g[k].map((x: string) => this.pretty(x))}));
      const n = this.groups.reduce((sum, x) => sum + x.items.length, 0);
      this.title = tool === 'list_permissions' ? `All permissions · ${n}` : `${n} permissions`;
      return;
    }
    if (tool === 'get_printer' && r?.id) {
      this.kind = 'printer'; this.icon = 'print'; this.title = 'Printer';
      this.fields = this.printerFields(r);
      return;
    }
    if (tool === 'get_printer_status' && results) {
      this.kind = 'printers'; this.icon = 'print';
      this.rows = results.map(p => ({...p, status: {online: !!p.is_online}, station: null,
        sub: p.last_seen ? 'Last seen ' + this.when(p.last_seen) : (p.status === 'unknown' ? 'No heartbeat yet' : p.status)}));
      this.title = `${results.length} ${results.length === 1 ? 'printer' : 'printers'} · status`;
      return;
    }
    if (tool === 'search_menu' && results) {
      this.kind = 'menu'; this.icon = 'restaurant_menu'; this.rows = results;
      this.title = `${results.length} menu ${results.length === 1 ? 'match' : 'matches'}`;
      return;
    }
    if (tool === 'list_printers' && results) {
      this.kind = 'printers'; this.icon = 'print'; this.rows = results;
      this.title = `${results.length} ${results.length === 1 ? 'printer' : 'printers'}`;
      return;
    }
    if (tool === 'get_sales_summary' && r) {
      this.kind = 'sales'; this.icon = 'insights';
      this.title = r.from === r.to ? `Sales · ${r.from}` : `Sales · ${r.from} → ${r.to}`;
      this.tiles = [
        {label: 'Net sales', value: this.money(r.net_sales)},
        {label: 'Orders', value: r.orders ?? '—'},
        {label: 'Average order', value: this.money(r.average_order)},
        {label: 'Tips', value: this.money(r.tips)},
        {label: 'Gross sales', value: this.money(r.gross_sales)},
        {label: 'Discounts', value: this.money(r.discounts)},
      ];
      return;
    }
    if (tool === 'get_store_status' && r) {
      this.kind = 'status'; this.icon = r.online_ordering === 'paused' ? 'pause_circle' : 'storefront';
      this.title = 'Online ordering';
      return;
    }
    if (tool === 'get_store_hours' && Array.isArray(r?.hours)) {
      this.kind = 'hours'; this.icon = 'schedule'; this.title = 'Store hours'; this.rows = r.hours;
      return;
    }
    if (tool === 'get_holidays' && Array.isArray(r?.holidays)) {
      this.kind = 'holidays' as Kind; this.icon = 'event'; this.title = 'Holidays'; this.rows = r.holidays;
      return;
    }
    if (tool === 'list_delivery_apps' && Array.isArray(r?.apps)) {
      this.kind = 'delivery'; this.icon = 'delivery_dining'; this.title = 'Delivery apps'; this.rows = r.apps;
      return;
    }
    if (tool === 'get_storefront' && r) {
      this.kind = 'storefront'; this.icon = 'language'; this.title = 'Ordering website';
      this.fields = Object.keys(r).map(k => ({label: this.label(k.replace(/^storefront_/, '')), value: r[k],
        swatch: k === 'storefront_brand_color'}));
      return;
    }
    if (results) {
      this.kind = 'list'; this.icon = 'list'; this.rows = results;
      const first = results[0] || {};
      this.nameKey = ['name', 'full_name', 'role_name', 'store_name', 'title'].find(k => k in first) || 'id';
      const noun = ({list_roles: 'roles', list_stores: 'stores', list_stations: 'POS stations'} as any)[tool];
      this.title = noun ? `${results.length} ${noun}` : `${this.label(tool)} · ${results.length}`;
      this.columns = Object.keys(first).filter(k => k !== 'id' && k !== this.nameKey && !k.endsWith('_id')
        && (first[k] === null || typeof first[k] !== 'object' || Array.isArray(first[k]) || first[k]?.name)).slice(0, 4);
      return;
    }
    // A write that went through: one clear line (never for a look-up: that shows its fields).
    if (!READ_TOOL.test(tool) && r && typeof r === 'object'
        && (r.done || r.id || r.name || r.preview || r.online !== undefined)) {
      this.kind = 'done'; this.icon = r.preview ? 'visibility' : 'check_circle';
      this.doneText = this.doneLine(tool, r);
      return;
    }
    if (r && typeof r === 'object') {
      const scalars = Object.keys(r).filter(k => k !== 'id' && r[k] !== null && r[k] !== '' && typeof r[k] !== 'object')
        .slice(0, 12);
      if (scalars.length) {
        this.kind = 'fields'; this.icon = 'info';
        this.title = r.name || r.full_name || r.title || this.label(tool.replace(READ_TOOL, ''));
        this.fields = scalars.map(k => ({label: this.label(k), value: this.when(r[k])}));
        return;
      }
      // only lists inside (e.g. routing options): one line per list
      const lists = Object.keys(r).filter(k => Array.isArray(r[k]));
      if (lists.length) {
        this.kind = 'fields'; this.icon = 'info'; this.title = this.label(tool.replace(READ_TOOL, ''));
        this.fields = lists.map(k => ({label: this.label(k), value: r[k].every((x: any) => typeof x !== 'object')
          ? (r[k].length > 8 ? `${r[k].slice(0, 8).join(', ')} … (${r[k].length})` : r[k].join(', ') || '—')
          : `${r[k].length}`}));
        return;
      }
    }
    this.kind = 'none';
  }

  roleName(r: any): string {
    const role = r?.role?.role_name || r?.role || '';
    return String(role).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }

  /** One employee: contact, POS, pay, stores — PIN shown only as set / not set. */
  private personFields(r: any) {
    const f: { label: string; value: any }[] = [];
    const add = (label: string, value: any) => { if (value !== null && value !== undefined && value !== '') { f.push({label, value}); } };
    add('Email', r.pin_only ? 'PIN only (no login)' : r.email);
    add('Phone', r.phone);
    add('POS PIN', r.has_pin ? 'Set' : 'Not set');
    add('Time clock', r.time_clock_tracked ? 'Tracked' : 'Not tracked');
    add('Hourly rate', r.hourly_rate !== null && r.hourly_rate !== undefined ? this.money(r.hourly_rate) + ' / hr' : null);
    add('Receipt name', r.receipt_name);
    add('Stores', Array.isArray(r.store_ids) && r.store_ids.length ? r.store_ids.join(', ') : null);
    add('Last login', r.last_login ? this.when(r.last_login) : 'Never');
    if (r.permissions && typeof r.permissions === 'object') {
      const n = Object.values(r.permissions).reduce((sum: number, list: any) => sum + (Array.isArray(list) ? list.length : 0), 0);
      add('Permissions', `${n}`);
    }
    return f;
  }

  /** 'POS_SETTINGS' / 'is_kitchen_printer' -> 'Pos settings' / 'Is kitchen printer'. */
  pretty(key: string): string {
    const t = String(key || '').replace(/[_.]/g, ' ').toLowerCase().trim();
    return t.charAt(0).toUpperCase() + t.slice(1);
  }

  /** One printer: what it prints, where, how many copies. */
  private printerFields(r: any) {
    const f: { label: string; value: any }[] = [];
    const add = (label: string, value: any) => { if (value !== null && value !== undefined && value !== '') { f.push({label, value}); } };
    const types = [r.is_sales_printer && 'Receipts', r.is_kitchen_printer && 'Kitchen tickets',
      r.is_operational_printer && 'Online & delivery orders'].filter(Boolean);
    add('Prints', types.length ? types.join(', ') : 'Nothing set');
    add('Station', r.station?.name || 'Not linked');
    add('Default printer', r.is_default ? 'Yes' : 'No');
    add('Hardware id', r.printer_id);
    if (r.is_kitchen_printer) {
      add('Ticket format', r.has_kitchen_format ? 'Kitchen format' : 'Full receipt');
      add('Kitchen language', r.kitchen_language_label);
    }
    const cats = r.menu_categories || [];
    add('Menu categories', cats.length ? (cats.length > 6 ? `${cats.slice(0, 6).join(', ')} … (${cats.length})` : cats.join(', ')) : 'All');
    add('Occasions', (r.occasions || []).map((o: string) => this.pretty(o)).join(', ') || 'All');
    const copies = r.copies && typeof r.copies === 'object'
      ? Object.keys(r.copies).filter(k => Number(r.copies[k]) > 0).map(k => `${this.pretty(k)} ${r.copies[k]}`) : [];
    add('Copies', copies.length ? copies.join(', ') : null);
    add('Last print', r.last_print_at ? this.when(r.last_print_at) : 'Never');
    return f;
  }

  /** ISO date/time -> 'Aug 7, 2025' (no Angular date pipe in this card). */
  when(value: any): any {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) { return value; }
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    return m >= 1 && m <= 12 ? `${MONTHS[m - 1]} ${d}, ${y}` : value;
  }

  private doneLine(tool: string, r: any): string {
    if (r.preview) { return String(r.preview); }
    if (typeof r.done === 'string') { return r.done; }
    if (tool === 'update_menu_item' || tool === 'create_menu_item') {
      const bits = [r.name];
      if (r.base_price !== undefined) { bits.push(this.money(r.base_price)); }
      if (r.is_available === false) { bits.push("86'd"); } else if (r.is_available === true) { bits.push('available'); }
      return `${tool === 'create_menu_item' ? 'Added' : 'Updated'}: ${bits.filter(Boolean).join(' · ')}`;
    }
    if (Array.isArray(r.results)) {
      const ok = r.results.filter((x: any) => x.success).length;
      return `${ok} of ${r.results.length} updated`;
    }
    const who = r.full_name || r.name || r.role_name || r.printer?.name || '';
    const verb = ([['deactivate', 'Deactivated'], ['reactivate', 'Reactivated'], ['delete', 'Deleted'], ['create', 'Added'],
      ['remove', 'Removed'], ['unlink', 'Unlinked'], ['link', 'Linked'], ['set_default', 'Default printer'],
      ['assign', 'Updated'], ['set_', 'Updated'], ['update', 'Updated'], ['pause', 'Paused'], ['resume', 'Resumed'],
      ['test_print', 'Test print sent'], ['reprint', 'Reprint sent']] as [string, string][])
      .find(([k]) => tool.includes(k))?.[1] || 'Done';
    if (typeof r.updated === 'number' || typeof r.created === 'number' || typeof r.deleted === 'number') {
      const n = r.updated ?? r.created ?? r.deleted;
      return `${verb}: ${n} ${n === 1 ? 'row' : 'rows'}`;
    }
    return who ? `${verb}: ${who}` : verb;
  }
}
